import type { Node as ProseNode } from '@milkdown/kit/prose/model';
import type { SerializerState } from '@milkdown/kit/transformer';
import { $node } from '@milkdown/kit/utils';
import type { CardType } from '@platform/markdown';
import { CARD_EMOJI_MAP } from '@platform/markdown';

const asCardType = (value: unknown): CardType => {
  if (value === 'win' || value === 'warning' || value === 'quote' || value === 'note') return value;
  return 'note';
};

const cardLabel = (cardType: CardType): string => {
  switch (cardType) {
    case 'win':
      return 'Win';
    case 'warning':
      return 'Warning';
    case 'quote':
      return 'Quote';
    case 'note':
      return 'Note';
  }
};

const cardEmojis = (cardType: CardType): string[] => {
  const fromShared = CARD_EMOJI_MAP[cardType] ?? [];
  if (cardType !== 'note') return fromShared;
  // Historically the editor used 🗒️ for notes; keep it recognized as a marker.
  return Array.from(new Set([...fromShared, '🗒️', '📋']));
};

const isParagraph = (node: ProseNode): boolean => node.type.name === 'paragraph';

const isMarkerOnlyParagraph = (text: string, cardType: CardType): boolean => {
  const trimmed = text.trim();
  if (!trimmed) return false;

  const emojis = cardEmojis(cardType).map((e) => e.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const emojiPattern = emojis.length > 0 ? `(?:${emojis.join('|')})` : '';
  const label = cardLabel(cardType);

  // Accept both real markdown ("**Quote:**") and escaped/literal ("\\*\\*Quote:\\*\\*").
  const strongLabelPattern = `(?:\\*\\*\\s*${label}\\s*:?\\s*\\*\\*|\\\\\\*\\\\\\*\\s*${label}\\s*:?\\\\\\*\\\\\\*)`;
  const plainLabelPattern = `${label}\\s*:?`;

  const pattern = new RegExp(
    `^${emojiPattern}\\s*(?:${strongLabelPattern}|${plainLabelPattern})?\\s*$`,
    'i'
  );

  return pattern.test(trimmed);
};

const startsWithMarker = (text: string, cardType: CardType): boolean => {
  const trimmed = text.trimStart();
  return cardEmojis(cardType).some((emoji) => trimmed.startsWith(emoji));
};

const hasAnyMarkerInFirstMeaningfulBlock = (blocks: ProseNode[], cardType: CardType): boolean => {
  const first = blocks.find((block) => block.textContent.trim() !== '');
  if (!first) return false;
  if (startsWithMarker(first.textContent, cardType)) return true;

  // Also accept label-only markers (e.g. "**Quote:**") when emoji was removed.
  const label = cardLabel(cardType);
  return new RegExp(`^\\s*(?:\\*\\*\\s*)?${label}\\s*:`, 'i').test(first.textContent);
};

const normalizeLeadingDuplicateMarkers = (blocks: ProseNode[], cardType: CardType): ProseNode[] => {
  if (blocks.length === 0) return blocks;

  // Identify the leading run of (marker-only paragraphs | empty paragraphs).
  let index = 0;
  let sawMarkerOnly = false;
  const prefixMarkerParagraphs: ProseNode[] = [];

  while (index < blocks.length) {
    const block = blocks[index];
    if (!isParagraph(block)) break;

    const text = block.textContent;
    if (text.trim() === '') {
      index += 1;
      continue;
    }

    if (isMarkerOnlyParagraph(text, cardType)) {
      sawMarkerOnly = true;
      prefixMarkerParagraphs.push(block);
      index += 1;
      continue;
    }

    break;
  }

  if (!sawMarkerOnly) return blocks;

  const rest = blocks.slice(index);
  const firstRest = rest.find((block) => block.textContent.trim() !== '');

  // If the remaining content already begins with an emoji marker + actual content,
  // drop the redundant marker-only prefix entirely.
  if (
    firstRest &&
    startsWithMarker(firstRest.textContent, cardType) &&
    !isMarkerOnlyParagraph(firstRest.textContent, cardType)
  ) {
    return rest;
  }

  // Otherwise, keep exactly one marker-only paragraph (the first one we saw).
  const marker = prefixMarkerParagraphs[0];
  return marker ? [marker, ...rest] : rest;
};

const emitHeaderParagraph = (state: SerializerState, cardType: CardType) => {
  const emoji = cardEmojis(cardType)[0] ?? '🗒️';
  const label = cardLabel(cardType);

  state.openNode('paragraph');
  state.addNode('text', undefined, `${emoji} `);
  state.openNode('strong');
  state.addNode('text', undefined, `${label}:`);
  state.closeNode();
  state.closeNode();
};

/**
 * ProseMirror node definition for single cards (emoji-prefixed blockquotes).
 *
 * A single card node is a blockquote with a cardType attribute that determines
 * its visual styling. It renders with appropriate styling in the editor and
 * serializes back to emoji-prefixed blockquote markdown.
 *
 * Card types:
 * - win: ✅ Success/achievement cards (green)
 * - warning: ⚠️ Alert/caution cards (amber)
 * - quote: 💬 Quotes/highlights (blue)
 * - note: 🗒️ General notes (neutral gray)
 */
export const singleCardNode = $node('card', () => {
  console.log('[singleCardNode] schema registered');
  return {
    // Content expression: standard blockquote content (one or more blocks)
    content: 'block+',

    // This is a block-level node
    group: 'block',

    // Allow it to be selected as a single unit
    selectable: true,

    // Allow this node to be dragged
    draggable: false,

    // Attributes
    attrs: {
      cardType: {
        default: 'note',
      },
    },

    // Parse HTML → ProseMirror node
    parseDOM: [
      {
        tag: 'div.card-block',
        priority: 52, // Higher than blockquote and grouped-cards
        getAttrs: (dom) => {
          if (typeof dom === 'string') return false;
          const el = dom as HTMLElement;

          // Extract card type from class names
          let cardType = 'note';
          if (el.classList.contains('card-win')) cardType = 'win';
          else if (el.classList.contains('card-warning')) cardType = 'warning';
          else if (el.classList.contains('card-quote')) cardType = 'quote';

          return { cardType };
        },
      },
    ],

    // Serialize ProseMirror node → HTML (for rendering in editor)
    toDOM: (node) => {
      const cardType = node.attrs.cardType || 'note';
      return [
        'div',
        {
          class: `card-block card-${cardType}`,
          'data-card-type': cardType,
        },
        0, // Content hole (children rendered here)
      ];
    },

    // Parse markdown → ProseMirror (handled by remarkSingleCard plugin)
    parseMarkdown: {
      match: (node) => {
        const mdNode = node as { type?: unknown; cardType?: string };
        return mdNode.type === 'card';
      },
      runner: (state, node, type) => {
        const mdNode = node as {
          type: string;
          cardType?: string;
          children?: any[];
          data?: { cardType?: string };
        };

        // cardType is stored as a direct property on the mdast node, not in data
        const cardType = mdNode.cardType || 'note';
        const children = mdNode.children || [];

        console.log('[singleCardNode] runner: cardType', cardType, 'children', children.length);

        state.openNode(type, { cardType });
        state.next(children);
        state.closeNode();
      },
    },

    // Serialize ProseMirror → markdown
    toMarkdown: {
      match: (node) => node.type.name === 'card',
      runner: (state, node) => {
        const cardType = asCardType(node.attrs.cardType);

        const blocks: ProseNode[] = [];
        node.content.forEach((child: ProseNode) => {
          blocks.push(child);
        });

        const normalizedBlocks = normalizeLeadingDuplicateMarkers(blocks, cardType);

        state.openNode('blockquote');

        // Ensure cards always serialize with a stable marker so they can be re-detected
        // when reloading markdown, but avoid duplicating markers on repeated saves.
        if (!hasAnyMarkerInFirstMeaningfulBlock(normalizedBlocks, cardType)) {
          emitHeaderParagraph(state, cardType);
        }

        for (const block of normalizedBlocks) {
          state.next(block);
        }

        state.closeNode();
      },
    },
  };
});
